import { Component, computed, inject, output, signal } from '@angular/core'
import { ModalHeaderComponent } from './modal-header/modal-header.component'
import { ModalBodyComponent } from './modal-body/modal-body.component'
import { ModalFooterComponent } from './modal-footer/modal-footer.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { EntityRef } from 'src/app/shared/models/referenceData'
import { EntitySelectorComponent } from '../entity-selector/entity-selector.component'
import { FormsModule } from '@angular/forms'
import { DateRangeFilterPickerComponent } from '../date-range-filter-picker/date-range-filter-picker.component'
import { DateRangeFilterStateValue } from '../../shared/services/filter/date-range-filter'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { EntityFilterConfig } from '../../shared/services/filter/entity-filter'

type ModalType = 'mdma' | 'tbill'

export type CreateRunModalState = Readonly<{
  operating_date?: DateRangeFilterStateValue
  billing_month?: DateRangeFilterStateValue
  settlement_type?: EntityRef
}>

export type CreateRunArgs = Readonly<{
  target: Date
  settlementType: string
}>

@Component({
  imports: [
    FormsModule,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
  ],
  selector: 'app-create-settlement-run-modal',
  template: `
    <div app-modal-header>Create Run</div>

    <div app-modal-body>
      @if (modalType() === 'mdma') {
          <div app-labeled-property label="Operating Day">
            <div dateRangeFilterPicker
              [value]="modalState().operating_date"
              (valueChange)="updateOperatingDate($event)"
              [showOperation]="false"
              [showRange]="false"
              [showTime]="false"
              [showSeconds]="false"
              [onlyMonth]="false"
            >
            </div>
          </div>
      } @else {
        <div app-labeled-property label="Billing Month">
          <div dateRangeFilterPicker
              [value]="modalState().billing_month"
              (valueChange)="updateBillingMonth($event)"
              [showOperation]="false"
              [showRange]="false"
              [showTime]="false"
              [showSeconds]="false"
              [onlyMonth]="true"
          >
          </div>
        </div>
      }
      <div app-labeled-property label="Settlement Type">
        <div
          app-entity-selector
          [slim]="true"
          label="Settlement Type"
          [config]="entityFilterConfig()"
          [value]="modalState().settlement_type"
          (valueChange)="updateSettlementType($event)"
        >
        </div>
      </div>
    </div>

    <div app-modal-footer>
      <button
        class="action-btn primary"
        [disabled]="!canRun()"
        (click)="run()"
      >
        Run
      </button>
      <button class="action-btn" (click)="modalService.dismissAll()">Cancel</button>
    </div>
  `,
})
export class CreateSettlementRunModalComponent {
  protected readonly modalService = inject(NgbModal)

  protected modalType = signal<ModalType>('mdma')

  protected entityFilterConfig = computed((): EntityFilterConfig => ({
    resource: this.modalType() === 'mdma' ? 'mdmaSettlementTypes' : 'tbillSettlementTypes',
    allowMultiple: false,
  }))

  protected readonly modalState = signal<CreateRunModalState>({})
  protected readonly onRun = output<CreateRunArgs>()

  protected readonly target = computed((): DateRangeFilterStateValue | undefined => {
    const { billing_month, operating_date } = this.modalState()
    return this.modalType() === 'mdma' ? operating_date : billing_month
  })

  protected canRun = computed(() =>
    Boolean(this.modalState().settlement_type && this.target()),
  )

  init(modalType: ModalType, onRun: (args: CreateRunArgs) => void, modalState: CreateRunModalState = {}) {
    this.modalType.set(modalType)
    this.onRun.subscribe(onRun)
    this.modalState.set(modalState)
  }

  updateOperatingDate(operating_date: DateRangeFilterStateValue) {
    this.modalState.update(v => ({ ...v, operating_date }))
  }

  updateBillingMonth(billing_month: DateRangeFilterStateValue){
    this.modalState.update(v => ({ ...v, billing_month }))
  }

  updateSettlementType(settlement_type: EntityRef) {
    this.modalState.update(v => ({ ...v, settlement_type }))
  }

  run() {
    this.onRun.emit({
      target: this.target()!.value as Date,
      settlementType: this.modalState().settlement_type!.entity_code,
    })
  }
}
